import { useState } from 'react'
import type {
  BackofficeUser,
  Surface,
  UserAdminDetailsResponse,
} from 'api/generated'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import {
  ACTIVITY_GROUPS,
  formatDateTime,
  formatDay,
  LOGIN_METHOD_NAMES,
  MATCH_STATUS_LABELS,
  timeAgo,
  yesNo,
} from 'helpers/user-details'
import { CLIENT_PLATFORM_LABELS, SOURCE_LABELS } from 'helpers/user-filters'
import { cn } from 'lib/utils'
import { ExternalLink, Loader } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store/store'
import { updateUser } from 'store/users/usersSlice'
import { SITE_URL } from './UserCells'
import { Fields, InfoTile, Section } from './UserDetailsParts'

type Details = UserAdminDetailsResponse

const Muted = ({ children }: { children: React.ReactNode }) => (
  <p className='text-sm text-muted-foreground'>{children}</p>
)

export const ActivityTab = ({ details }: { details: Details }) => (
  <div className='space-y-6'>
    {ACTIVITY_GROUPS.map((group) => (
      <Section key={group.title} title={group.title}>
        <div className='overflow-hidden rounded-lg border'>
          <table className='w-full table-fixed text-sm'>
            <thead className='bg-muted/50 text-xs text-muted-foreground'>
              <tr>
                <th className='px-3 py-2 text-left font-medium'>Metrica</th>
                <th className='w-16 px-2 py-2 text-right font-medium'>
                  Totale
                </th>
                <th className='w-16 px-2 py-2 text-right font-medium'>30 gg</th>
                <th className='hidden w-28 px-3 py-2 text-right font-medium sm:table-cell'>
                  Ultima
                </th>
              </tr>
            </thead>
            <tbody>
              {group.metrics.map(({ key, label }) => {
                const counter = details.activity[key]
                return (
                  <tr
                    key={key}
                    className={cn(
                      'border-t',
                      !counter.total && 'text-muted-foreground'
                    )}
                  >
                    <td className='px-3 py-1.5'>
                      {label}
                      {/* on phones the date goes under the label */}
                      {counter.last_at && (
                        <div className='text-xs text-muted-foreground sm:hidden'>
                          {timeAgo(counter.last_at)}
                        </div>
                      )}
                    </td>
                    <td className='px-2 py-1.5 text-right font-medium tabular-nums'>
                      {counter.total}
                    </td>
                    <td className='px-2 py-1.5 text-right tabular-nums'>
                      {counter.last_30_days || ''}
                    </td>
                    <td className='hidden px-3 py-1.5 text-right text-muted-foreground sm:table-cell'>
                      {counter.last_at ? formatDay(counter.last_at) : ''}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Section>
    ))}

    <Section title='Trova partita: partite in cui ha un posto'>
      <div className='flex flex-wrap gap-2'>
        {Object.entries(details.match_finder_statuses).map(
          ([status, count]) => (
            <Badge key={status} variant={count ? 'default' : 'outline'}>
              {MATCH_STATUS_LABELS[status as keyof typeof MATCH_STATUS_LABELS]}:{' '}
              {count}
            </Badge>
          )
        )}
      </div>
    </Section>
  </div>
)

export const MembershipTab = ({
  user,
  details,
}: {
  user: BackofficeUser
  details: Details
}) => {
  const admin = details.user.admin
  const { codes, subscriptions, checkouts } = details.membership
  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
        <InfoTile
          label='Premium'
          value={admin?.premium ? 'Attivo' : 'Non attivo'}
          tone={admin?.premium ? 'good' : undefined}
          hint={
            user.premium && !admin?.premium ? 'flag on, scaduto' : undefined
          }
        />
        <InfoTile
          label='Scadenza'
          value={formatDay(user.premium_expiration)}
          hint={timeAgo(user.premium_expiration)}
        />
        <InfoTile
          label='Codici attivati'
          value={admin?.premium_purchases_count ?? 0}
          hint={`${codes.length} in totale`}
        />
        <InfoTile
          label='Checkout Shopify'
          value={`${checkouts.used} / ${checkouts.total}`}
          hint={
            checkouts.last_at
              ? `ultimo ${timeAgo(checkouts.last_at)}`
              : 'conclusi / aperti'
          }
        />
      </div>

      <Section title='Abbonamenti Recharge'>
        {subscriptions.length ? (
          <div className='space-y-2'>
            {subscriptions.map((subscription) => (
              <div
                key={subscription.id}
                className={cn(
                  'space-y-3 rounded-lg border p-3 text-sm',
                  subscription.in_recovery &&
                    'border-amber-300 bg-amber-50 dark:bg-amber-500/10'
                )}
              >
                <div className='flex flex-wrap items-center gap-2'>
                  <Badge
                    variant={
                      subscription.status === 'active' ? 'default' : 'outline'
                    }
                  >
                    {subscription.status}
                  </Badge>
                  {subscription.in_recovery && (
                    <Badge variant='destructive'>Pagamento fallito</Badge>
                  )}
                  <span className='text-muted-foreground'>
                    #{subscription.recharge_subscription_id ?? '—'} · dal{' '}
                    {formatDay(subscription.created_at)}
                  </span>
                </div>
                <Fields
                  items={[
                    [
                      'Prossimo addebito',
                      formatDay(subscription.next_charge_at),
                    ],
                    ['Collegato tramite', subscription.linked_via],
                    ['Primo ordine', subscription.first_shopify_order_id],
                    [
                      'Carta',
                      [
                        subscription.card_payment_type,
                        subscription.card_expires_on &&
                          `scade ${formatDay(subscription.card_expires_on)}`,
                      ]
                        .filter(Boolean)
                        .join(' · '),
                    ],
                    ['Motivo errore', subscription.failure_cause],
                  ]}
                />
              </div>
            ))}
          </div>
        ) : (
          <Muted>Nessun abbonamento.</Muted>
        )}
      </Section>

      <Section title='Codici membership'>
        {codes.length ? (
          <div className='space-y-2'>
            {codes.map((code) => (
              <div
                key={code.id}
                className='flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border p-2 text-sm'
              >
                <span className='font-mono'>{code.code}</span>
                <Badge variant='outline'>{code.plan}</Badge>
                {code.activated ? (
                  <Badge>Attivato</Badge>
                ) : (
                  <Badge variant='secondary'>Da attivare</Badge>
                )}
                <span className='text-muted-foreground'>
                  {code.paid
                    ? `ordine ${code.shopify_order_id}`
                    : 'manuale / omaggio'}{' '}
                  · {formatDay(code.created_at)}
                  {code.activation_rule && ` · regola ${code.activation_rule}`}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <Muted>Nessun codice.</Muted>
        )}
      </Section>
    </div>
  )
}

export const FitTab = ({ details }: { details: Details }) => {
  const player = details.fit_player
  const requests = details.fit_card_requests
  return (
    <div className='space-y-6'>
      {player ? (
        <>
          <div className='flex flex-wrap items-start justify-between gap-2'>
            <div>
              <div className='flex items-center gap-2 text-lg font-semibold'>
                <Badge variant='outline' className='font-mono text-base'>
                  {player.rank}
                </Badge>
                {player.name}
              </div>
              <Muted>
                {[
                  player.club?.name,
                  player.club?.province,
                  player.category,
                  player.age && `${player.age} anni`,
                  player.card_number && `tessera ${player.card_number}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Muted>
            </div>
            <Button variant='outline' size='sm' asChild>
              <a
                href={`${SITE_URL}/giocatore/${player.id}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <ExternalLink /> Pagina giocatore
              </a>
            </Button>
          </div>

          <div className='flex flex-wrap gap-1 empty:hidden'>
            {!player.active && (
              <Badge variant='secondary'>Non tesserato quest’anno</Badge>
            )}
            {player.hidden && (
              <Badge variant='destructive'>Nascosto su richiesta</Badge>
            )}
            {!player.ita && <Badge variant='secondary'>Straniero</Badge>}
          </div>

          <div className='grid grid-cols-2 gap-2 sm:grid-cols-4'>
            <InfoTile
              label={`Punti FIT ${player.fit_year ?? ''}`}
              value={player.info.fit_points ?? '—'}
              hint={
                player.info.simulated_rank &&
                `classifica live ${player.info.simulated_rank}`
              }
            />
            <InfoTile
              label='Vinte / perse'
              value={`${player.matches.won ?? 0} / ${player.matches.lost ?? 0}`}
              hint={
                player.matches.won_percentage !== undefined &&
                `${Math.round(player.matches.won_percentage)}% vinte`
              }
            />
            <InfoTile
              label='Miglior classifica'
              value={player.max_rank.rank ?? '—'}
              hint={player.max_rank.year}
            />
            <InfoTile
              label='Visite al profilo'
              value={details.activity.fit_profile_visits_received.total}
              hint={`+${details.activity.fit_profile_visits_received.last_30_days} in 30 gg`}
            />
          </div>

          {!!player.state_of_form?.length && (
            <Section title='Stato di forma'>
              <div className='flex gap-1'>
                {player.state_of_form.map((won, index) => (
                  <span
                    key={index}
                    className={cn(
                      'flex size-6 items-center justify-center rounded text-xs font-bold text-white',
                      won ? 'bg-lime-600' : 'bg-red-500'
                    )}
                  >
                    {won ? 'V' : 'P'}
                  </span>
                ))}
              </div>
            </Section>
          )}

          <Section title='Ultime partite'>
            {player.last_matches.length ? (
              <ul className='divide-y rounded-lg border text-sm'>
                {/* Match and UserMatch ids can collide: the index keeps keys unique */}
                {player.last_matches.map((match, index) => (
                  <li
                    key={`${index}-${match.id}`}
                    className='flex items-center justify-between gap-2 px-3 py-1.5'
                  >
                    <span className='min-w-0 truncate'>
                      vs {match.first_opponent?.name ?? '—'}
                      {match.second_opponent &&
                        ` / ${match.second_opponent.name}`}
                      {match.first_opponent?.rank &&
                        ` (${match.first_opponent.rank})`}
                    </span>
                    <span
                      className={cn(
                        'shrink-0 font-mono',
                        match.has_won ? 'text-lime-700' : 'text-red-600'
                      )}
                    >
                      {match.result ?? '—'}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <Muted>Nessuna partita.</Muted>
            )}
          </Section>

          {player.active_competitions.length > 0 && (
            <Section title='Tornei in corso'>
              <ul className='space-y-1 text-sm'>
                {player.active_competitions.map((competition) => (
                  <li key={competition.id}>
                    {competition.name}{' '}
                    <span className='text-muted-foreground'>
                      {competition.from} – {competition.to}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </>
      ) : (
        <Muted>Nessun profilo FIT collegato.</Muted>
      )}

      <Section title='Richieste di collegamento tessera'>
        {requests.length ? (
          <ul className='space-y-2 text-sm'>
            {requests.map((request) => (
              <li key={request.id} className='rounded-lg border p-2'>
                <div className='flex flex-wrap items-center gap-2'>
                  <Badge
                    variant={
                      request.status === 'approved'
                        ? 'default'
                        : request.status === 'rejected'
                          ? 'destructive'
                          : 'secondary'
                    }
                  >
                    {request.status === 'approved'
                      ? 'Approvata'
                      : request.status === 'rejected'
                        ? 'Rifiutata'
                        : 'In attesa'}
                  </Badge>
                  <span>
                    {request.player_name ?? '—'} ·{' '}
                    {request.fit_player_id ?? 'senza tessera'}
                  </span>
                  <span className='text-muted-foreground'>
                    {formatDay(request.created_at)}
                    {request.backfilled && ' · storica'}
                  </span>
                </div>
                {request.notes && (
                  <p className='mt-1 text-xs text-muted-foreground'>
                    {request.notes}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Muted>Nessuna richiesta.</Muted>
        )}
      </Section>
    </div>
  )
}

export const AccessTab = ({ details }: { details: Details }) => {
  const admin = details.user.admin
  const { sessions } = details
  return (
    <div className='space-y-6'>
      <Section title='Iscrizione e ultimo login'>
        <Fields
          items={[
            ['Iscritto il', formatDateTime(admin?.created_at)],
            [
              'Iscrizione da',
              admin?.signup_source ? SOURCE_LABELS[admin.signup_source] : null,
            ],
            [
              'Metodo iscrizione',
              admin?.signup_method
                ? LOGIN_METHOD_NAMES[admin.signup_method]
                : null,
            ],
            [
              'Client iscrizione',
              [
                admin?.signup_client_platform &&
                  CLIENT_PLATFORM_LABELS[admin.signup_client_platform],
                admin?.signup_client_version,
              ]
                .filter(Boolean)
                .join(' '),
            ],
            ['Primo accesso', formatDateTime(admin?.first_sign_in_at)],
            ['Ultimo login', formatDateTime(admin?.last_sign_in_at)],
            [
              'Metodo ultimo login',
              admin?.last_sign_in_method
                ? LOGIN_METHOD_NAMES[admin.last_sign_in_method]
                : null,
            ],
            [
              'Ultimo login da',
              admin?.last_sign_in_source
                ? SOURCE_LABELS[admin.last_sign_in_source]
                : null,
            ],
            [
              'Ultimo client',
              [
                admin?.client_platform &&
                  CLIENT_PLATFORM_LABELS[admin.client_platform],
                admin?.client_version,
              ]
                .filter(Boolean)
                .join(' '),
            ],
            [
              'Token push',
              admin?.platforms.length ? admin.platforms.join(', ') : null,
            ],
            ['Login social', admin?.login_method === 'social' ? 'Sì' : 'No'],
            ['Ultima attività', formatDateTime(admin?.last_active_at)],
          ]}
        />
      </Section>

      <Section title={`Sessioni attive (${sessions.count})`}>
        {sessions.list.length ? (
          <ul className='space-y-2 text-sm'>
            {sessions.list.map((session) => (
              <li key={session.id} className='rounded-lg border p-2'>
                <div className='flex flex-wrap items-center gap-2'>
                  <Badge variant='outline'>
                    {session.client_platform
                      ? CLIENT_PLATFORM_LABELS[session.client_platform]
                      : session.source
                        ? SOURCE_LABELS[session.source]
                        : 'Non tracciata'}
                    {session.client_version && ` ${session.client_version}`}
                  </Badge>
                  {session.auth_method && (
                    <span>{LOGIN_METHOD_NAMES[session.auth_method]}</span>
                  )}
                  <span className='text-muted-foreground'>
                    aperta {formatDay(session.created_at)} · attiva{' '}
                    {timeAgo(session.last_active_at)}
                  </span>
                </div>
                {session.user_agent && (
                  <p className='mt-1 truncate text-xs text-muted-foreground'>
                    {session.user_agent}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <Muted>
            Nessuna sessione: l’utente non è loggato su nessun dispositivo.
          </Muted>
        )}
        <Muted>
          Le sessioni si cancellano al logout, all’attivazione premium e al
          collegamento della tessera. Metodo, provenienza e versione esistono
          solo da ottobre 2026: prima sono vuoti.
        </Muted>
      </Section>
    </div>
  )
}

const SURFACE_LABELS: Record<Surface, string> = {
  clay_court: 'Terra rossa',
  synthetic: 'Sintetico',
  grass_court: 'Erba',
  synthetic_grass: 'Erba sintetica',
  hard_court: 'Cemento',
  synthetic_clay: 'Terra sintetica',
  other: 'Altro',
}

export const AccountTab = ({ user }: { user: BackofficeUser }) => {
  const dispatch = useAppDispatch()
  const saving = useAppSelector((state) => state.users.loading)
  const [draft, setDraft] = useState({
    first_name: user.first_name,
    last_name: user.last_name,
    email: user.email,
  })
  const changed =
    draft.first_name !== user.first_name ||
    draft.last_name !== user.last_name ||
    draft.email !== user.email
  const professional = [
    user.teacher &&
      `Maestro${user.teacher.qualification ? ` (${user.teacher.qualification})` : ''}`,
    user.trainer &&
      `Preparatore${user.trainer.qualification ? ` (${user.trainer.qualification})` : ''}`,
    user.nutritionist && 'Nutrizionista',
    user.mental_coach && 'Mental coach',
    user.shop_profile && `Negozio ${user.shop_profile.name ?? ''}`,
    user.club_manager && `Gestore ${user.club_manager.club?.name ?? 'circolo'}`,
  ].filter(Boolean)
  const notifications = Object.entries(user.notification_settings ?? {})

  return (
    <div className='space-y-6'>
      <Section title='Modifica'>
        <form
          className='grid gap-3 sm:grid-cols-3'
          onSubmit={(event) => {
            event.preventDefault()
            dispatch(updateUser({ userId: user.id, data: draft }))
          }}
        >
          {(
            [
              ['first_name', 'Nome'],
              ['last_name', 'Cognome'],
              ['email', 'Email'],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className='flex flex-col gap-1.5'>
              <Label htmlFor={`user-${key}`}>{label}</Label>
              <Input
                id={`user-${key}`}
                type={key === 'email' ? 'email' : 'text'}
                autoComplete='off'
                value={draft[key]}
                onChange={(e) =>
                  setDraft((current) => ({ ...current, [key]: e.target.value }))
                }
              />
            </div>
          ))}
          <div className='sm:col-span-3'>
            <Button type='submit' disabled={!changed || saving}>
              {saving && <Loader className='animate-spin' />} Salva
            </Button>
          </div>
        </form>
      </Section>

      <Section title='Profilo'>
        <Fields
          items={[
            ['Nascita', formatDay(user.birth_date)],
            ['Telefono', user.phone],
            [
              'Indirizzo',
              [user.address?.city, user.address?.province]
                .filter(Boolean)
                .join(' '),
            ],
            [
              'Superficie preferita',
              user.preferred_surface
                ? SURFACE_LABELS[user.preferred_surface]
                : null,
            ],
            ['Ruolo', user.role],
            ['UID social', user.uid],
            ['Risultati nascosti', yesNo(user.hidden_results)],
            ['Mercatino accettato', yesNo(user.market_condition_accepted)],
            ['Marketing', yesNo(user.marketing_communication_check)],
            ['Comunicazioni TT', yesNo(user.tennistalker_communication_check)],
            ['Allievi ambassador', user.ambassador_students],
            ['Profili professionali', professional.join(', ')],
          ]}
        />
        {user.bio && (
          <p className='rounded-lg bg-muted/50 p-2 text-sm whitespace-pre-line'>
            {user.bio}
          </p>
        )}
      </Section>

      {(user.is_banned || user.banned_at) && (
        <Section title='Ban'>
          <Fields
            items={[
              ['Bannato il', formatDateTime(user.banned_at)],
              ['Da', user.banned_by],
              ['Motivo', user.ban_notes],
            ]}
          />
        </Section>
      )}

      {notifications.length > 0 && (
        <Section title='Notifiche'>
          <div className='flex flex-wrap gap-1'>
            {notifications.map(([key, enabled]) => (
              <Badge key={key} variant={enabled ? 'default' : 'outline'}>
                {key.replaceAll('_', ' ')}
              </Badge>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}
